import { useState } from 'react';
import { uniq } from 'lodash';
import { LoadingWrapper } from '@ardoq/status-ui';
import { ErrorBoundary } from '@ardoq/error-boundary';
import {
  LazyLoadResults,
  LazyPaginationController,
  Pagination,
} from '@ardoq/pagination';
import { GremlinResultsTable } from './GremlinResultsTable/GremlinResultsTable';
import { GremlinResultsColumnsSelect } from './GremlinResultsColumnsSelect';
import { getResultType } from './utils';
import { GremlinResult, GremlinResultType } from './types';
import { RESULTS_PER_PAGE } from './consts';
import { getUniqueSearchId } from '../utils';
import { logError } from '@ardoq/logging';
import { InlineText } from '@ardoq/typography';
import { FlexBox } from '@ardoq/layout';
import { spacing } from '@ardoq/design-tokens';
import { GremlinRawResultsSegmentedControl } from './GremlinRawResultsSegmentedControl';
import type { FieldLabelByName } from 'models/utils/fieldOps';
import { t, type I18nLocale } from '@ardoq/i18n';
import { type Locale } from '@ardoq/locale';
import { getPaginationCoreLabels } from 'locales/getPaginationLabels';
import { getGremlinEmptyStateLabels } from './emptyStateLabels';

const getResultKeys = (results: GremlinResult[]) => {
  try {
    return uniq(results.flatMap(result => Object.keys(result)));
  } catch (e) {
    return [];
  }
};

type GremlinResultsProps = {
  result?: GremlinResult[];
  printable?: boolean;
  totalResults?: number | null;
  loadResults?: LazyLoadResults;
  resultsId?: string;
  showRaw?: boolean;
  style?: React.CSSProperties;
  usePagination?: boolean;
  surroundingPagesToCache: number;
  showRawResultsSegmentedControl?: boolean;
  onRawResultsSelectionChange?: VoidFunction;
  fieldLabelByName: FieldLabelByName;
  isFilteredBySearch?: boolean;
  locale: Locale;
  i18nLocale: I18nLocale;
};

const GremlinResults = ({
  result,
  printable,
  totalResults,
  loadResults,
  resultsId,
  showRaw,
  style,
  usePagination,
  surroundingPagesToCache,
  showRawResultsSegmentedControl,
  onRawResultsSelectionChange,
  fieldLabelByName,
  isFilteredBySearch = false,
  locale,
  i18nLocale,
}: GremlinResultsProps) => {
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);

  const updateSelectedColumns = (input: string[] | null) => {
    if (input === null) {
      setSelectedColumns([]);
    } else {
      setSelectedColumns(input);
    }
  };

  const resultKeys =
    result && getResultType(result) === GremlinResultType.MAP
      ? getResultKeys(result)
      : [];

  const emptyStateLabels = getGremlinEmptyStateLabels({
    i18nLocale,
    isFilteredBySearch,
  });

  return (
    <>
      {!printable && (
        <FlexBox justify="space-between" paddingBottom="small" align="end">
          <FlexBox>
            <InlineText variant="text1">
              {t(
                i18nLocale,
                'search:gremlinResults.numberOfHits',
                'Number of hits:'
              )}{' '}
            </InlineText>
            <InlineText variant="text1Bold">&nbsp;{totalResults}</InlineText>
          </FlexBox>
          <FlexBox gap={'medium'} align={'end'}>
            {!showRaw && (
              <FlexBox>
                <GremlinResultsColumnsSelect
                  results={result || []}
                  selectedColumns={selectedColumns}
                  onSelectedColumnsChange={updateSelectedColumns}
                  fieldLabelByName={fieldLabelByName}
                  i18nLocale={i18nLocale}
                />
              </FlexBox>
            )}
            {showRawResultsSegmentedControl && onRawResultsSelectionChange && (
              <GremlinRawResultsSegmentedControl
                selectedId={showRaw ? 'raw' : 'table'}
                onChange={onRawResultsSelectionChange}
                i18nLocale={i18nLocale}
              />
            )}
          </FlexBox>
        </FlexBox>
      )}
      <ErrorBoundary
        logError={logError}
        stackTraceLabel={t(
          i18nLocale,
          'errorBoundary:stackTraceLabel',
          'Stack trace'
        )}
      >
        <LazyPaginationController
          perPage={RESULTS_PER_PAGE}
          dataSource={result || []}
          totalResults={totalResults || 0}
          loadResults={loadResults || (() => {})}
          resultsId={resultsId || getUniqueSearchId()}
          surroundingPagesToCache={surroundingPagesToCache}
          render={({
            currentPageDataSource,
            currentPageNumber,
            perPage,
            totalResults,
            sortById,
            sortOrder,
            sortBy,
            onPageSelect,
            isLoading,
          }) => (
            <>
              <LoadingWrapper loading={isLoading} style={style}>
                <GremlinResultsTable
                  results={
                    !printable || usePagination
                      ? currentPageDataSource
                      : result || []
                  }
                  selectedColumns={selectedColumns}
                  resultKeys={resultKeys}
                  showRaw={showRaw}
                  printable={printable}
                  sortBy={sortBy}
                  sortById={sortById}
                  sortOrder={sortOrder}
                  fieldLabelByName={fieldLabelByName}
                  emptyStateLabels={emptyStateLabels}
                  locale={locale}
                  i18nLocale={i18nLocale}
                />
              </LoadingWrapper>
              {(!printable || usePagination) && totalResults > perPage && (
                <Pagination
                  style={{
                    justifyContent: 'center',
                    padding: `${spacing.s8} 0 ${spacing.s4}`,
                  }}
                  currentPageNumber={currentPageNumber}
                  perPage={perPage}
                  totalResults={totalResults}
                  onPageSelect={onPageSelect}
                  {...getPaginationCoreLabels(i18nLocale)}
                />
              )}
            </>
          )}
        />
      </ErrorBoundary>
    </>
  );
};

export { GremlinResults };
